<script context="module">
</script>

<script>
  import { forceEncodingToSystem } from "../util/encodings.js";
  import { createEventDispatcher, getContext, onMount } from "svelte";
  import ErrorText from "./ErrorText.svelte";
  import { encodingToHex } from "bitframes";
  import {
    currentMintData,
    loadHighlight,
    mintButton,
    refreshButton,
    userState,
  } from "../util/highlight-loader.js";
  import { ethers } from "ethers";
  import SignInButton from "./SignInButton.svelte";
  import {
    CONTRACTS,
    displayEther,
    ETH_PRICE,
    isAfterLaunchStore,
    isBeforeLaunchStore,
    LINK_ABOUT,
    LINK_EDITIONS,
    LINK_TERMS,
    MIN_ETH_PRICE_WITH_FEE_STRING,
    PLATFORM_FEE_ETH,
  } from "../util/constants.js";
  import LinkButton from "./LinkButton.svelte";
  import ExternalLink from "./ExternalLink.svelte";
  import ArtworkThumb from "./ArtworkThumb.svelte";
  import Loader from "./Loader.svelte";
  import CountdownButton from "./CountdownButton.svelte";
  import Paragraph from "./Paragraph.svelte";
  import AfterCloseButton from "./AfterCloseButton.svelte";
  import InlineSpan from "./InlineSpan.svelte";
  import { fetchTokenDetails } from "../util/data-fetcher.js";
  import RefreshUpdateError from "./RefreshUpdateError.svelte";

  export let editionID = -1;

  const encodingStore = getContext("encoding");
  const TIMER_HACK = true;
  let timers = [];
  const clearTimers = () => {
    timers.forEach((t) => clearTimeout(t));
    timers.length = 0;
  };

  let validCustomPrice = true;
  const dispatch = createEventDispatcher();
  const donationMultiplierAmounts = [
    { value: 1, label: "1x" },
    // { value: 2, label: "2x" },
    { value: 5, label: "5x" },
    { value: 10, label: "10x" },
    // { value: 10, label: "10x" },
    // { value: 32, label: "32x" },
    // { value: 16, label: "16x" },
    { value: "custom", label: "...", custom: true },
  ];

  let customPrice = ETH_PRICE;
  let donationMultiplier = 1;
  let currentPrice = ETH_PRICE;
  const MAX_MULTIPLIER = 100;
  const MAX_PRICE = 10;
  $: {
    let oldPrice = currentPrice;
    if (donationMultiplier == "custom") {
      currentPrice = Math.max(
        ETH_PRICE,
        Math.min(MAX_PRICE, parseFloat(customPrice) || ETH_PRICE)
      );
    } else {
      customPrice = ETH_PRICE;
      let mult = donationMultiplier || 1;
      if (!isFinite(mult)) mult = 1;
      mult = Math.max(1, Math.min(MAX_MULTIPLIER, mult));
      currentPrice = ETH_PRICE * mult;
    }
    if (oldPrice !== currentPrice) {
      updateSettings();
    }
  }

  let loaded = false;
  let curEncoding;
  let curSeedHex;
  let unsubEncoding;
  if (encodingStore) {
    unsubEncoding = encodingStore.subscribe((e) => {
      e = forceEncodingToSystem(e, 0x01);
      curSeedHex = encodingToHex(e);
      curEncoding = e;
      updateSettings();
    });
  }

  let curUserState;
  let unsubUserState = userState.subscribe((s) => {
    curUserState = s;
    if (s && s.walletAddress) {
      updateSettings();
    }
  });
  // let interval = setInterval(() => updateSettings(), 100);

  let curTokenInfo,
    _lastId = null;

  async function fetchTokenInfo() {
    curTokenInfo = await fetchTokenDetails({
      token_id: editionID,
      contract: CONTRACTS.NFT_LIMITED.address,
    });
  }

  $: {
    const newID = editionID;
    if (newID !== _lastId) {
      console.log("Fetching token info");
      fetchTokenInfo();
      _lastId = newID;
    }
  }

  let errState = "Loading...";
  $: {
    console.log("TOKEN INFO", curTokenInfo);
    if (curTokenInfo && curUserState) {
      if (
        curTokenInfo.owned_address.toLowerCase() !==
        curUserState.walletAddress.toLowerCase()
      ) {
        // If token owner isn't correct
        errState = "Only the owner can update this encoding.";
      } else if (curSeedHex === curTokenInfo.seed) {
        // If the target seed is the same
        errState = "The new encoding is the same as the old.";
      } else {
        errState = null;
      }
    } else {
      errState = "Loading...";
    }
  }

  // const mintEvent = (event) => {
  //   const publisher = curUserState.walletAddress;
  //   const encoding = curSeedHex;
  //   dispatch("mint", { ...event.detail, publisher, encoding });
  // };
  onMount(() => {
    // window.addEventListener("highlight:minted", mintEvent, { passive: true });
    load();
    updateSettings();
    return () => {
      clearTimers();
      unsubUserState();
      unsubEncoding();
      // window.removeEventListener("highlight:minted", mintEvent);
    };
  });

  function updateSettings() {
    const donationAmount = String(currentPrice);
    currentMintData.update((d) => {
      return {
        ...d,
        donationAmount,
        encodingHex: curSeedHex,
      };
    });

    console.log("Dispatch - seed:", curSeedHex, "price:", donationAmount);

    const detail = {
      collectionId: CONTRACTS.NFT_LIMITED.id,
      seed: `0x${curSeedHex}`,
      tokenId: ethers.toBeHex(editionID),
    };
    const runDispatch = () => {
      // console.log("sending...");
      window.dispatchEvent(
        new CustomEvent(`highlight:update-seed-refresh-state`, {
          detail,
        })
      );
    };

    clearTimers();

    runDispatch();
    if (TIMER_HACK) {
      // absolute hack for it to work when signing out and back in
      timers.push(setTimeout(() => runDispatch(), 10));
      timers.push(setTimeout(() => runDispatch(), 500));
      timers.push(setTimeout(() => runDispatch(), 1000));
    }
  }

  async function load() {
    await loadHighlight();
    loaded = true;
    updateSettings();
  }

  function useWithRefreshButton(node, opts = {}) {
    refreshButton.init();
    refreshButton.attach(node);
    return {
      destroy() {
        refreshButton.detach();
      },
    };
  }
</script>

<div class="mint" class:invalid={!validCustomPrice}>
  {#await load()}
    <div>Loading...</div>
    <Loader />
  {:then}
    <div class="thumb-row">
      <div class="thumb">
        <ArtworkThumb hd encoding={curEncoding} />
      </div>
      <div class="info-column">
        If you are the owner of <InlineSpan
          ><strong>32 Bit Edition #{editionID}</strong></InlineSpan
        >, you can update its on-chain encoding to this new design. Note this
        will trigger a new blockchain transaction with gas fees.
      </div>
    </div>

    <!-- <div class="info">
      To mint your design as a token in the gallery, you will need an <ExternalLink
        href="https://ethereum.org/en/wallets/">Ethereum wallet</ExternalLink
      >. The minimum contribution is
      <strong class="inline">{ETH_PRICE} ETH</strong>, but higher payments will
      give you a better chance of winning one of the limited
      <LinkButton external to={`/${LINK_ABOUT}#edition`}
        >32 BIT EDITIONS</LinkButton
      >.
    </div> -->
    <div class="info subtext">
      This is only applicable for owners of one of the 32 Bit Edition tokens.
      See the <LinkButton external to={`/${LINK_EDITIONS}`}>EDITIONS</LinkButton
      > page for more details.
    </div>
    <div class="signed-in-container">
      <div class="signed-in-row">
        <div class="sign-in-container">
          <SignInButton />
        </div>
      </div>
    </div>
    {#if curUserState && curUserState.walletAddress}
      {#if !errState}
        <div
          role="none"
          class="mint-button-container"
          use:useWithRefreshButton
        ></div>
      {:else}
        <RefreshUpdateError text={errState} />
      {/if}
    {/if}

    {#if true}
      <div class="lower-container">
        <div class="left">
          {#if curUserState && curUserState.walletAddress}
            Updating <strong>32 Bit Edition #{editionID}</strong>
          {/if}
        </div>
        <div class="right">
          <LinkButton external to={`/${LINK_TERMS}`}
            >Terms & Conditions</LinkButton
          >
        </div>
      </div>
    {/if}
  {:catch err}
    <div>Could not load Highlight mint embed. Please try again later.</div>
    <ErrorText>ERORR: {err.message}</ErrorText>
  {/await}
</div>

<style>
  .inline {
    display: inline-flex;
  }
  .mint {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px;
    box-sizing: border-box;
    /* max-width: 500px; */
  }
  .subtext {
    font-size: 12px;
  }
  .invalid .mint-button-container {
    cursor: auto !important;
    pointer-events: none !important;
    position: relative;
  }
  .invalid .mint-button-container::after {
    content: " ";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 0, 0, 0.75);
  }
  .mint-one {
    font-style: italic;
    /* border-top: 1px dotted black; */
    /* padding-top: 10px; */
    /* font-size: 12px; */
  }
  .signed-in-row {
    display: flex;
    /* flex-direction: column; */
    gap: 10px;
    /* flex-grow: 1; */
    justify-content: space-between;
    align-items: center;
  }
  .contribution-factor {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
  }

  .contribution-factor-button {
    background: transparent;
    border: 1px solid black;
    border-radius: 0;
    padding: 0;
    appearance: none;
    margin: 0;
    width: 36px;
    height: 36px;
    font-size: 13px;
  }
  .contribution-factor-button.active {
    background-color: var(--accent-color);
  }

  .lower-container {
    font-size: 11px;
    width: 100%;
    flex-grow: 1;
    justify-content: space-between;
    align-items: flex-start;
    display: flex;
    margin-top: -15px;
    gap: 10px;
  }
  .left {
    /* flex-basis: 70%; */
    /* flex-grow: 1; */
  }
  .custom-price-container {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
    margin-top: 20px;
  }
  .eth-price {
    flex-grow: 1;
    text-align: right;
    padding: 5px 10px;
    font: inherit;
    width: 50px;
    border-radius: 0;
    border: 1px solid hsl(0, 0%, 0%);
  }
  .eth-price[readonly] {
    border: 1px solid transparent;
  }
  .mint-button-container {
    min-height: 48px;
    /* max-width: 128px; */
    /* max-height: 32px; */
    /* overflow: hidden; */
  }
  .sign-in-container {
    flex-shrink: 0;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    gap: 20px;
    min-height: 36px;
  }

  /* :global(.highlight-1i2y9aa .highlight-qo1k2::after) {
    content: var(--mint-amount-eth) !important;
    display: inline-flex !important;
    padding-left: 5px;
  }
  :global(.highlight-16ivil5[disabled] .highlight-qo1k2::after) {
    content: "foo" !important;
    display: inline-flex !important;
    padding-left: 5px;
  } */
  .thumb-row {
    display: flex;
    gap: 20px;
    /* flex-direction: column; */
    justify-content: space-evenly;
    /* font-size: 13px; */
  }
  .thumb {
    flex-shrink: 0;
    width: 160px;
    height: 160px;
  }
  @media screen and (max-width: 800px) {
    .thumb {
      width: 110px;
      height: 110px;
    }
  }
  @media screen and (max-width: 600px) {
    .lower-container {
      margin-top: -5px;
      font-size: 11px;
    }
    .info-column {
      font-size: 13px;
    }
    .subtext {
      font-size: 13px;
    }
    .custom-price-container {
      justify-content: flex-end;
      margin-top: 10px;
      /* font-size: 11px; */
    }
  }
</style>
